import React, { useState, useEffect } from 'react';
import SuperAdminSidebar from '../../../components/hostel/superadmin/SuperAdminSidebar';
import SuperAdminNav from '../../../components/hostel/superadmin/SuperAdminNav';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const SuperAdminDashboard = ({ onNavigate, userInfo }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);

  const getUserName = () => {
    if (!userInfo) return 'Not Available';
    return userInfo.fullName || userInfo.full_name || userInfo.name || 'Not Available';
  };

  const getUserInitial = () => {
    const name = getUserName();
    if (name === 'Not Available') return '?';
    return name.charAt(0).toUpperCase();
  };

  const userName = getUserName();
  const userInitial = getUserInitial();

  useEffect(() => {
    console.log('👤 SuperAdmin UserInfo:', userInfo);
    console.log('📝 User Name:', userName);
    if (userInfo?.userId) {
      fetchDashboardData();
    }
  }, [userInfo]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/superadmin/dashboard/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setDashboardData(data);
        console.log('✅ Dashboard data loaded:', data);
      } else {
        setError(data.message || 'Failed to load dashboard data');
      }
    } catch (err) {
      console.error('❌ Error fetching dashboard:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const notifications = dashboardData?.notifications || [];

  const getMetrics = () => {
    if (!dashboardData?.stats) {
      return [];
    }

    const stats = dashboardData.stats;

    return [
      {
        label: 'Total Hostels',
        value: stats.total_hostels?.toString() || '0',
        total: '10',
        percentage: stats.total_hostels ? Math.round((stats.total_hostels / 10) * 100) : 0,
        emoji: '🏢',
        trend: stats.hostel_trend || 'N/A'
      },
      {
        label: 'Total Students',
        value: stats.total_students?.toString() || '0',
        total: '1000',
        percentage: stats.total_students ? Math.round((stats.total_students / 1000) * 100) : 0,
        emoji: '👥',
        trend: stats.student_trend || 'N/A'
      },
      {
        label: 'Active Admins',
        value: stats.total_admins?.toString() || '0',
        total: '20',
        percentage: stats.total_admins ? Math.round((stats.total_admins / 20) * 100) : 0,
        emoji: '👨‍💼',
        trend: stats.admin_trend || 'N/A'
      },
      {
        label: 'Pending Applications',
        value: stats.pending_applications?.toString() || '0',
        total: '100',
        percentage: stats.pending_applications ? Math.round((stats.pending_applications / 100) * 100) : 0,
        emoji: '📋',
        trend: stats.application_trend || 'N/A'
      },
      {
        label: 'Total Grievances',
        value: stats.total_grievances?.toString() || '0',
        total: '200',
        percentage: stats.total_grievances ? 45 : 0,
        emoji: '💬',
        trend: stats.grievance_trend || 'N/A'
      },
      {
        label: 'Total Revenue',
        value: stats.total_revenue ? `₹${Number(stats.total_revenue / 100000).toFixed(1)}L` : '₹0',
        total: '₹50L',
        percentage: stats.total_revenue ? 78 : 0,
        emoji: '💰',
        trend: stats.revenue_trend || 'N/A'
      },
      {
        label: 'Occupancy Rate',
        value: stats.occupancy_rate ? `${stats.occupancy_rate.toFixed(0)}%` : '0%',
        total: '100%',
        percentage: stats.occupancy_rate || 0,
        emoji: '📊',
        trend: stats.occupancy_trend || 'N/A'
      },
      {
        label: 'System Health',
        value: stats.system_health ? `${stats.system_health}%` : '0%',
        total: '100%',
        percentage: stats.system_health || 0,
        emoji: '✅',
        trend: stats.health_trend || 'N/A'
      }
    ];
  };

  const SimpleChart = () => {
    const chartData = dashboardData?.revenueData || [];

    if (!chartData || chartData.length === 0) {
      return (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#666',
          fontSize: '16px'
        }}>
          No revenue data available
        </div>
      );
    }

    const maxValue = Math.max(...chartData.map(d => d.value || 0));
    const height = 250;
    const width = 100;
    const padding = 40;

    return (
      <div className="chart-container">
        <svg viewBox={`0 0 ${width * chartData.length} ${height}`} className="chart-svg">
          {[0, 1, 2, 3, 4].map((i) => (
            <line
              key={i}
              x1="0"
              y1={padding + (i * (height - padding * 2) / 4)}
              x2={width * chartData.length}
              y2={padding + (i * (height - padding * 2) / 4)}
              stroke="rgba(82,171,152,0.15)"
              strokeWidth="1"
            />
          ))}

          <polygon
            points={[
              `0,${height - padding}`,
              ...chartData.map((d, i) =>
                `${i * width + width / 2},${height - padding - (((d.value || 0) / maxValue) * (height - padding * 2))}`
              ),
              `${(chartData.length - 1) * width + width / 2},${height - padding}`
            ].join(' ')}
            fill="rgba(82, 171, 152, 0.15)"
          />

          <polyline
            points={chartData.map((d, i) =>
              `${i * width + width / 2},${height - padding - (((d.value || 0) / maxValue) * (height - padding * 2))}`
            ).join(' ')}
            fill="none"
            stroke="#52ab98"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {chartData.map((d, i) => (
            <circle
              key={i}
              cx={i * width + width / 2}
              cy={height - padding - (((d.value || 0) / maxValue) * (height - padding * 2))}
              r="5"
              fill="white"
              stroke="#52ab98"
              strokeWidth="3"
            />
          ))}
        </svg>

        <div className="chart-labels">
          {chartData.map((d, i) => (
            <span key={i}>{d.month}</span>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'Inter, sans-serif',
        fontSize: '18px',
        color: '#2b6777',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{ fontSize: '48px' }}>⏳</div>
        <div>Loading SuperAdmin Dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'Inter, sans-serif',
        gap: '20px'
      }}>
        <div style={{ fontSize: '48px' }}>❌</div>
        <div style={{ fontSize: '18px', color: '#2b6777' }}>{error}</div>
        <button
          onClick={fetchDashboardData}
          style={{
            padding: '12px 24px',
            background: '#52ab98',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600'
          }}
        >
          🔄 Retry
        </button>
      </div>
    );
  }

  const metrics = getMetrics();
  const hostels = dashboardData?.hostels || [];
  const activities = dashboardData?.recentActivities || [];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          -webkit-font-smoothing: antialiased;
          overflow-x: hidden;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(2deg); }
        }

        @keyframes glow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        .dashboard-container {
          display: flex;
          height: 100vh;
          background: #ffffff;
          position: relative;
          overflow: hidden;
        }

        .sidebar {
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          width: 300px;
          background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(242,242,242,0.98) 100%);
          backdrop-filter: blur(30px);
          border-right: 2px solid rgba(82,171,152,0.2);
          transform: translateX(-100%);
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 100;
          display: flex;
          flex-direction: column;
          box-shadow: 8px 0 40px rgba(43,103,119,0.15);
        }

        .sidebar.open {
          transform: translateX(0);
        }

        .sidebar-header {
          padding: 36px 28px;
          background: linear-gradient(135deg, rgba(43,103,119,0.05) 0%, rgba(82,171,152,0.05) 100%);
          border-bottom: 2px solid rgba(82,171,152,0.15);
          position: relative;
          overflow: hidden;
        }

        .sidebar-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(82,171,152,0.15), transparent);
          animation: shimmer 4s infinite;
        }

        .sidebar-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 24px;
          position: relative;
          z-index: 1;
        }

        .close-btn {
          background: rgba(43,103,119,0.08);
          border: 1.5px solid rgba(82,171,152,0.25);
          cursor: pointer;
          font-size: 20px;
          color: #2b6777;
          width: 38px;
          height: 38px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .close-btn:hover {
          background: rgba(43,103,119,0.15);
          border-color: #52ab98;
          transform: rotate(90deg);
        }

        .user-info-sidebar {
          display: flex;
          align-items: center;
          gap: 14px;
          position: relative;
          z-index: 1;
        }

        .user-avatar-sidebar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 22px;
          color: white;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
          border: 3px solid rgba(255, 255, 255, 0.5);
        }

        .user-details-sidebar {
          flex: 1;
        }

        .user-name-sidebar {
          font-size: 16px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .user-role-sidebar {
          font-size: 13px;
          color: #52ab98;
          font-weight: 600;
        }

        .sidebar-nav {
          flex: 1;
          padding: 28px 20px;
          overflow-y: auto;
        }

        .sidebar-nav::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-nav::-webkit-scrollbar-track {
          background: rgba(82,171,152,0.08);
        }

        .sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(82,171,152,0.3);
          border-radius: 3px;
        }

        .nav-section {
          margin-bottom: 32px;
        }

        .nav-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: rgba(43,103,119,0.5);
          margin-bottom: 12px;
          padding-left: 18px;
        }

        .menu-item {
          position: relative;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 18px;
          margin-bottom: 10px;
          cursor: pointer;
          color: #2b6777;
          font-size: 15px;
          font-weight: 600;
          border-radius: 14px;
          transition: all 0.3s;
          overflow: hidden;
        }

        .menu-glow {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
          opacity: 0;
          transition: opacity 0.3s;
        }

        .menu-item:hover {
          color: #52ab98;
          background: rgba(82,171,152,0.1);
          transform: translateX(6px);
          box-shadow: 0 4px 16px rgba(82,171,152,0.15);
        }

        .menu-item:hover .menu-glow {
          opacity: 1;
        }

        .menu-item-active {
          background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
          color: #2b6777;
          box-shadow: 0 6px 20px rgba(82,171,152,0.2);
          font-weight: 700;
        }

        .menu-item-active .menu-glow {
          opacity: 1;
          animation: glow 2s ease-in-out infinite;
        }

        .menu-icon {
          font-size: 22px;
          position: relative;
          z-index: 1;
        }

        .menu-label {
          position: relative;
          z-index: 1;
        }

        .logout-section {
          padding: 20px;
          border-top: 2px solid rgba(82,171,152,0.15);
        }

        .logout-btn {
          width: 100%;
          padding: 16px 20px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.3s;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
        }

        .logout-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 35px rgba(43,103,119,0.4);
        }

        .overlay {
          position: fixed;
          inset: 0;
          background: rgba(43,103,119,0.4);
          backdrop-filter: blur(6px);
          z-index: 90;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
        }

        .overlay.show {
          opacity: 1;
          visibility: visible;
        }

        .main-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          width: 100%;
          position: relative;
          z-index: 1;
        }

        .top-header {
          background: #ffffff;
          padding: 52px 32px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid rgba(82,171,152,0.12);
          position: relative;
          box-shadow: 0 2px 8px rgba(43,103,119,0.06);
        }

        .top-header::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 24px;
          flex: 1;
        }

        .menu-toggle {
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          border: 2px solid rgba(82,171,152,0.25);
          cursor: pointer;
          font-size: 26px;
          color: #2b6777;
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
          box-shadow: 0 4px 20px rgba(43,103,119,0.15);
        }

        .menu-toggle:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 30px rgba(43,103,119,0.25);
          background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
        }

        .header-title-section {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .header-greeting {
          font-size: 18px;
          color: #52ab98;
          font-weight: 600;
        }

        .header-name {
          font-size: 28px;
          font-weight: 900;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .user-avatar-header {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 20px;
          color: white;
          cursor: pointer;
          border: 3px solid rgba(82,171,152,0.3);
          transition: all 0.3s;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
        }

        .user-avatar-header:hover {
          transform: scale(1.1);
          box-shadow: 0 8px 32px rgba(43,103,119,0.5);
        }

        .content-wrapper {
          flex: 1;
          overflow-y: auto;
          padding: 40px 28px;
        }

        .content-wrapper::-webkit-scrollbar {
          width: 8px;
        }

        .content-wrapper::-webkit-scrollbar-track {
          background: rgba(82,171,152,0.08);
        }

        .content-wrapper::-webkit-scrollbar-thumb {
          background: rgba(82,171,152,0.3);
          border-radius: 4px;
        }

        .content-area {
          max-width: 1500px;
          margin: 0 auto;
          animation: slideIn 0.6s ease-out;
        }

        .hero-section {
          background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
          border-radius: 24px;
          padding: 48px 36px;
          margin-bottom: 40px;
          border: 1px solid rgba(82,171,152,0.15);
          box-shadow: 0 2px 8px rgba(43,103,119,0.06);
          position: relative;
          overflow: hidden;
        }

        .hero-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
        }

        .hero-content {
          position: relative;
          z-index: 1;
        }

        .hero-title {
          font-size: 36px;
          font-weight: 900;
          color: #2b6777;
          margin-bottom: 10px;
          letter-spacing: -0.5px;
        }

        .hero-subtitle {
          font-size: 16px;
          color: rgba(43,103,119,0.6);
          font-weight: 500;
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
          margin-bottom: 40px;
        }

        .metric-card {
          background: #ffffff;
          border-radius: 24px;
          padding: 28px;
          border: 1px solid rgba(43,103,119,0.12);
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(43,103,119,0.06);
        }

        .metric-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
          transform: scaleY(0);
          transition: transform 0.3s ease;
        }

        .metric-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(43,103,119,0.12);
          border-color: rgba(82,171,152,0.25);
        }

        .metric-card:hover::before {
          transform: scaleY(1);
        }

        .metric-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 20px;
        }

        .metric-label-section {
          flex: 1;
        }

        .metric-label {
          font-size: 12px;
          color: rgba(43,103,119,0.6);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          margin-bottom: 4px;
        }

        .metric-trend {
          font-size: 13px;
          font-weight: 700;
          color: #52ab98;
        }

        .metric-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          background: rgba(82,171,152,0.1);
          border: 1.5px solid rgba(82,171,152,0.2);
        }

        .metric-value {
          font-size: 32px;
          font-weight: 900;
          color: #2b6777;
          margin-bottom: 12px;
          letter-spacing: -0.5px;
          line-height: 1;
        }

        .metric-progress {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .progress-bar {
          flex: 1;
          height: 8px;
          background: rgba(82,171,152,0.15);
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
          border-radius: 4px;
          transition: width 1s ease;
        }

        .progress-text {
          font-size: 12px;
          font-weight: 700;
          color: rgba(43,103,119,0.6);
        }

        .chart-section {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 20px;
          margin-bottom: 40px;
        }

        .chart-card {
          background: rgba(255,255,255,0.9);
          backdrop-filter: blur(30px);
          border-radius: 24px;
          padding: 32px;
          border: 2px solid rgba(82,171,152,0.2);
          box-shadow: 0 8px 32px rgba(43,103,119,0.12);
        }

        .chart-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 24px;
        }

        .chart-title {
          font-size: 22px;
          font-weight: 900;
          color: #2b6777;
        }

        .chart-badge {
          padding: 8px 16px;
          background: rgba(82,171,152,0.1);
          border-radius: 10px;
          font-size: 12px;
          font-weight: 700;
          color: #2b6777;
          border: 1.5px solid rgba(82,171,152,0.2);
        }

        .chart-container {
          width: 100%;
        }

        .chart-svg {
          width: 100%;
          height: 250px;
        }

        .chart-labels {
          display: flex;
          justify-content: space-around;
          margin-top: 12px;
          font-size: 13px;
          font-weight: 600;
          color: rgba(43,103,119,0.6);
        }

        .hostels-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .hostel-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: rgba(82,171,152,0.08);
          border-radius: 16px;
          border: 1.5px solid rgba(82,171,152,0.15);
          transition: all 0.3s;
        }

        .hostel-item:hover {
          background: rgba(82,171,152,0.15);
          border-color: rgba(82,171,152,0.25);
          transform: translateX(6px);
        }

        .hostel-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 800;
          font-size: 20px;
        }

        .hostel-info {
          flex: 1;
        }

        .hostel-name {
          font-size: 15px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .hostel-stats {
          font-size: 13px;
          color: rgba(43,103,119,0.6);
          font-weight: 500;
        }

        .hostel-occupancy {
          font-size: 16px;
          font-weight: 900;
          color: #52ab98;
        }

        .bottom-section {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .activity-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }

        .activity-item {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          padding: 16px;
          background: rgba(82,171,152,0.08);
          border-radius: 16px;
          border: 1.5px solid rgba(82,171,152,0.15);
          transition: all 0.3s;
        }

        .activity-item:hover {
          background: rgba(82,171,152,0.15);
          transform: translateX(6px);
        }

        .activity-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: #52ab98;
          margin-top: 6px;
          flex-shrink: 0;
          box-shadow: 0 0 12px rgba(82,171,152,0.5);
        }

        .activity-content {
          flex: 1;
        }

        .activity-text {
          font-size: 14px;
          font-weight: 600;
          color: #2b6777;
          margin-bottom: 6px;
        }

        .activity-time {
          font-size: 12px;
          color: rgba(43,103,119,0.5);
          font-weight: 600;
        }

        .quick-actions {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }

        .action-btn {
          padding: 18px 20px;
          background: rgba(82,171,152,0.08);
          color: #2b6777;
          border: 1.5px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          transition: all 0.3s;
        }

        .action-btn:hover {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border-color: transparent;
          transform: translateX(6px);
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
        }

        /* Desktop view - Sidebar always visible */
        @media (min-width: 1024px) {
          .sidebar {
            position: static;
            transform: translateX(0);
          }

          .close-btn {
            display: none;
          }

          .menu-toggle {
            display: none;
          }

          .overlay {
            display: none !important;
          }

          .metrics-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        /* Mobile specific styles */
        @media (max-width: 1023px) {
          .close-btn {
            display: block;
          }

          .sidebar {
            width: 280px;
          }
        }

        @media (max-width: 1280px) {
          .chart-section {
            grid-template-columns: 1fr;
          }
        }

        /* Tablet and Mobile */
        @media (max-width: 768px) {
          .top-header {
            padding: 20px 16px;
          }

          .header-greeting {
            font-size: 13px;
          }

          .header-name {
            font-size: 18px;
          }

          .menu-toggle,
          .user-avatar-header {
            width: 44px;
            height: 44px;
            font-size: 20px;
          }

          .content-wrapper {
            padding: 24px 16px;
          }

          .hero-section {
            padding: 28px 20px;
            margin-bottom: 24px;
          }

          .hero-title {
            font-size: 24px;
          }

          .hero-subtitle {
            font-size: 14px;
          }

          .metrics-grid {
            grid-template-columns: 1fr;
            gap: 16px;
            margin-bottom: 24px;
          }

          .metric-card {
            padding: 20px;
          }

          .metric-icon {
            width: 48px;
            height: 48px;
            font-size: 24px;
          }

          .metric-value {
            font-size: 28px;
          }

          .chart-section {
            gap: 16px;
            margin-bottom: 24px;
          }

          .chart-card {
            padding: 20px;
          }

          .chart-title {
            font-size: 18px;
          }

          .chart-badge {
            font-size: 11px;
            padding: 6px 12px;
          }

          .bottom-section {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .sidebar-header {
            padding: 24px 20px;
          }

          .user-avatar-sidebar {
            width: 48px;
            height: 48px;
            font-size: 20px;
          }

          .hostel-item {
            padding: 14px;
          }

          .hostel-icon {
            width: 40px;
            height: 40px;
            font-size: 16px;
          }

          .activity-item {
            padding: 14px;
          }

          .action-btn {
            padding: 16px;
          }
        }

        /* Extra small mobile devices */
        @media (max-width: 480px) {
          .header-name {
            font-size: 16px;
          }

          .header-greeting {
            font-size: 12px;
          }

          .hero-title {
            font-size: 20px;
          }

          .hero-subtitle {
            font-size: 13px;
          }

          .metric-value {
            font-size: 24px;
          }

          .chart-title {
            font-size: 16px;
          }
        }
      `}</style>

      <div className="dashboard-container">
        <SuperAdminSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          userInfo={userInfo}
          onNavigate={onNavigate}
          activePage="hostel-superadmin-dashboard"
        />

        <div
          className={`overlay ${sidebarOpen ? 'show' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />

        <div className="main-content">
          <SuperAdminNav
            setSidebarOpen={setSidebarOpen}
            userInfo={userInfo}
          />

          <div className="content-wrapper">
            <div className="content-area">
              <div className="hero-section">
                <div className="hero-content">
                  <h1 className="hero-title">System Overview 🎯</h1>
                  <p className="hero-subtitle">
                    {dashboardData?.stats?.total_hostels
                      ? `Managing ${dashboardData.stats.total_hostels} hostel${dashboardData.stats.total_hostels > 1 ? 's' : ''} with complete system control`
                      : 'No hostel data available'}
                  </p>
                </div>
              </div>

              <div className="metrics-grid">
                {metrics.map((metric, index) => (
                  <div key={index} className="metric-card">
                    <div className="metric-header">
                      <div className="metric-label-section">
                        <div className="metric-label">{metric.label}</div>
                        <div className="metric-trend">{metric.trend}</div>
                      </div>
                      <div className="metric-icon">{metric.emoji}</div>
                    </div>
                    <div className="metric-value">{metric.value}</div>
                    <div className="metric-progress">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${metric.percentage}%` }}
                        />
                      </div>
                      <span className="progress-text">{metric.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="chart-section">
                <div className="chart-card">
                  <div className="chart-header">
                    <h3 className="chart-title">System-Wide Revenue Trend</h3>
                    <div className="chart-badge">Last 6 Months</div>
                  </div>
                  <SimpleChart />
                </div>

                <div className="chart-card">
                  <div className="chart-header">
                    <h3 className="chart-title">All Hostels</h3>
                  </div>
                  <div className="hostels-list">
                    {hostels.length > 0 ? hostels.map((hostel, index) => {
                      const totalRooms = hostel.total_rooms || 0;
                      const availableRooms = hostel.available_rooms || 0;
                      const occupiedRooms = totalRooms - availableRooms;
                      const occupancyRate = totalRooms > 0
                        ? Math.round((occupiedRooms / totalRooms) * 100)
                        : 0;

                      return (
                        <div key={index} className="hostel-item">
                          <div className="hostel-icon">
                            {hostel.hostel_name?.charAt(0) || 'H'}
                          </div>
                          <div className="hostel-info">
                            <div className="hostel-name">{hostel.hostel_name || 'Unknown Hostel'}</div>
                            <div className="hostel-stats">
                              {totalRooms} room{totalRooms !== 1 ? 's' : ''} • {hostel.hostel_type || 'N/A'}
                            </div>
                          </div>
                          <div className="hostel-occupancy">
                            {occupancyRate}%
                          </div>
                        </div>
                      );
                    }) : (
                      <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666', fontSize: '15px' }}>
                        No hostel data available
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bottom-section">
                <div className="chart-card">
                  <h3 className="chart-title">Recent System Activities</h3>
                  <div className="activity-list">
                    {activities.length > 0 ? activities.slice(0, 4).map((activity, index) => (
                      <div key={index} className="activity-item">
                        <div className="activity-dot" />
                        <div className="activity-content">
                          <div className="activity-text">{activity.activity_text}</div>
                          <div className="activity-time">
                            {new Date(activity.activity_time).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666', fontSize: '15px' }}>
                        No recent activities available
                      </div>
                    )}
                  </div>
                </div>

                <div className="chart-card">
                  <h3 className="chart-title">Quick Actions</h3>
                  <div className="quick-actions">
                    <button
                      className="action-btn"
                      onClick={() => onNavigate && onNavigate('hostel-superadmin-manage-hostel')}
                    >
                      Add New Hostel
                      <span>🏢</span>
                    </button>
                    <button
                      className="action-btn"
                      onClick={() => onNavigate && onNavigate('hostel-superadmin-manage-admin')}
                    >
                      Manage Admins
                      <span>👨‍💼</span>
                    </button>
                    <button
                      className="action-btn"
                      onClick={() => onNavigate && onNavigate('hostel-superadmin-manage-students')}
                    >
                      View All Students
                      <span>👥</span>
                    </button>
                    <button
                      className="action-btn"
                      onClick={() => onNavigate && onNavigate('hostel-superadmin-manage-grievance')}
                    >
                      Review Grievances
                      <span>💬</span>
                    </button>
                    {/* 🆕 NEW MANAGE LAUNDRY BUTTON */}
                    <button
                      className="action-btn"
                      onClick={() => onNavigate && onNavigate('hostel-superadmin-manage-laundry')}
                    >
                      Manage Laundry
                      <span>🧺</span>
                    </button>
                    <button
                      className="action-btn"
                      onClick={() => onNavigate && onNavigate('hostel-superadmin-rent')}
                    >
                      Manage Rents
                      <span>💰</span>
                    </button>
                    {/* 🆕 NEW MANAGE ROOMS BUTTON */}
                    <button
                      className="action-btn"
                      onClick={() => onNavigate && onNavigate('hostel-superadmin-manage-rooms')}
                    >
                      Manage Rooms
                      <span>🏠</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SuperAdminDashboard;